import * as z from "zod";

const idNameSchema = z.object({ id: z.string(), name: z.string() });

/** Product- vs return-kind split of an inventory storage count. */
export const processingPipelineStorageCountsSchema = z.object({
  productCount: z.number(),
  returnCount: z.number(),
  total: z.number(),
});

export type ProcessingPipelineStorageCounts = z.infer<
  typeof processingPipelineStorageCountsSchema
>;

export const getProcessingPipeline = {
  name: "getProcessingPipeline" as const,
  input: z.object({
    returnsCenterId: z.string().optional(),
    teamIds: z.array(z.string()).optional(),
  }),
  output: z.object({
    /** The operator team's returns center(s), for the header / RC selector. */
    returnsCenterOptions: z.array(idNameSchema),
    /** In-transit returns toward the scoped RC(s) — context card, not backlog. */
    inTransitCount: z.number(),
    /**
     * All arrived-but-unprocessed returns in the scoped RC(s) — the inbound
     * label is delivered and there is no completed processing session.
     */
    awaitingTotal: z.number(),
    /**
     * Inventory items whose source return has a `warehouse_return_processed`
     * `return_event` in the scoped RC(s) that have **never been placed into a
     * `bin` or `storage` location** and are still alive at the warehouse.
     * Excludes items currently in `external` / `lost` / `flush` / `waste`.
     */
    awaitingStorage: processingPipelineStorageCountsSchema,
    /**
     * Inventory items physically shelved in a `bin` or `storage` location in
     * the scoped RC(s) with positive on-hand quantity.
     */
    inStorage: processingPipelineStorageCountsSchema,
    /** Every merchant with returns/items in the pipeline, for the filter dropdown. */
    merchantOptions: z.array(idNameSchema),
    /** Server time the snapshot was computed (ISO) — drives the freshness label. */
    generatedAt: z.string(),
  }),
};

export type GetProcessingPipelineOutput = z.infer<
  (typeof getProcessingPipeline)["output"]
>;
